import { Bot, Hand, MoonStar, UserRoundCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { describeApiError } from '@/api/problemDetails'
import { Badge, Button, toast } from '@/components/ui'
import { useSession } from '@/features/auth/session'

import { useTicketAction } from './queries'
import { formatWaitingTime } from './ticketFormatting'
import type { DeskTicket } from './types'

interface TicketBannerProps {
  readonly tenantId: string
  readonly conversationId: number
  readonly ticket: DeskTicket
}

/**
 * Faixa do CHAMADO ativo no topo da conversa (docs/design/07 §8): protocolo, motivo, resumo do agente, quem está com
 * a conversa e as duas ações do atendente — assumir e devolver para a IA. Deixa explícito que a IA está pausada aqui.
 */
export function TicketBanner({ tenantId, conversationId, ticket }: TicketBannerProps) {
  const { t } = useTranslation()
  const { user } = useSession()
  const action = useTicketAction(tenantId, conversationId)
  const assignedToMe = user !== null && ticket.assignedUserId === user.id
  const waiting = ticket.status === 'waiting'

  // Rotulos dos motivos (enum da Api); motivo novo sem traducao cai no generico.
  const reasonLabels: Readonly<Record<string, string>> = {
    UserRequestedHuman: t('desk.ticket.reasons.UserRequestedHuman'),
    LowConfidence: t('desk.ticket.reasons.LowConfidence'),
    CriticalIssue: t('desk.ticket.reasons.CriticalIssue'),
    FinancialIssue: t('desk.ticket.reasons.FinancialIssue'),
    Complaint: t('desk.ticket.reasons.Complaint'),
    SensitiveRequest: t('desk.ticket.reasons.SensitiveRequest'),
    PolicyBlocked: t('desk.ticket.reasons.PolicyBlocked'),
    MissingInformation: t('desk.ticket.reasons.MissingInformation'),
    AgentFailure: t('desk.ticket.reasons.AgentFailure'),
    OperatorTakeover: t('desk.ticket.reasons.OperatorTakeover'),
  }

  function run(kind: 'take-over' | 'return-to-agent'): void {
    action.mutate(kind, {
      onSuccess: () => {
        toast.success(kind === 'take-over' ? t('desk.ticket.tookOver') : t('desk.ticket.returned'))
      },
      onError: (error) => {
        toast.error(t('desk.ticket.actionFailed'), {
          description: describeApiError(error).join(' '),
        })
      },
    })
  }

  return (
    <section
      aria-label={t('desk.ticket.label', { number: ticket.number })}
      className="flex shrink-0 flex-col gap-2 border-b border-status-warning/40 bg-status-warning-subtle p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground-primary">
            <Hand aria-hidden="true" className="h-4 w-4 text-status-warning" />
            {t('desk.ticket.label', { number: ticket.number })}
          </span>
          <Badge variant={waiting ? 'warning' : 'info'}>
            {waiting ? t('desk.ticket.statusWaiting') : t('desk.ticket.statusInProgress')}
          </Badge>
          <span className="text-xs text-foreground-muted">
            {t('desk.ticket.openedAgo', { time: formatWaitingTime(ticket.createdAt, t) })}
          </span>
          {ticket.openedWhileUnavailable ? (
            <span className="inline-flex items-center gap-1 text-xs text-foreground-muted">
              <MoonStar aria-hidden="true" className="h-3.5 w-3.5" />
              {t('desk.ticket.openedOffline')}
            </span>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2">
          {!assignedToMe ? (
            <Button
              size="sm"
              variant="primary"
              loading={action.isPending && action.variables === 'take-over'}
              disabled={action.isPending}
              onClick={() => {
                run('take-over')
              }}
            >
              <UserRoundCheck aria-hidden="true" className="h-4 w-4" />
              {t('desk.ticket.takeOver')}
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="secondary"
            loading={action.isPending && action.variables === 'return-to-agent'}
            disabled={action.isPending}
            onClick={() => {
              run('return-to-agent')
            }}
          >
            <Bot aria-hidden="true" className="h-4 w-4" />
            {t('desk.ticket.returnToAgent')}
          </Button>
        </div>
      </div>

      <dl className="grid gap-1 text-xs text-foreground-secondary sm:grid-cols-[auto_1fr] sm:gap-x-3">
        <dt className="font-medium text-foreground-muted">{t('desk.ticket.reason')}</dt>
        <dd>{reasonLabels[ticket.reason] ?? t('desk.ticket.reasons.other')}</dd>
        {ticket.summary !== null ? (
          <>
            <dt className="font-medium text-foreground-muted">{t('desk.ticket.summary')}</dt>
            <dd className="whitespace-pre-wrap break-words">{ticket.summary}</dd>
          </>
        ) : null}
        <dt className="font-medium text-foreground-muted">{t('desk.ticket.owner')}</dt>
        <dd>
          {assignedToMe
            ? t('desk.ticket.ownerMe')
            : ticket.assignedToName !== null
              ? ticket.assignedToName
              : t('desk.ticket.ownerNobody')}
        </dd>
      </dl>

      <p className="text-xs text-foreground-muted">{t('desk.ticket.aiPaused')}</p>
    </section>
  )
}
